import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { resolveCoverUrl } from '../../services/api';
import styles from './StudyMenuPage.module.css';
import { AlertIcon } from '../../components/Icons';
import LoadingSpinner from '../../components/LoadingSpinner';
import StudyMenu from './StudyMenu';

const StudyMenuPage = () => {
  const navigate = useNavigate();

  const [favoriteBooks, setFavoriteBooks] = useState([]);
  const [menuWordGroups, setMenuWordGroups] = useState([]);
  const [menuSentenceGroups, setMenuSentenceGroups] = useState([]);
  const [menuNoteGroups, setMenuNoteGroups] = useState([]);
  const [bookMetadata, setBookMetadata] = useState({});
  const [bookMetadataLoading, setBookMetadataLoading] = useState(false);
  const [bookMetadataError, setBookMetadataError] = useState(null);
  const [stats, setStats] = useState({
    totalBooks: 0,
    totalWords: 0,
    totalSentences: 0,
    totalNotes: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const cardConfigs = useMemo(() => ([
    {
      key: 'books',
      title: '书籍',
      count: stats.totalBooks,
      icon: <span className="material-icons-round">auto_stories</span>,
    },
    {
      key: 'words',
      title: '单词',
      count: stats.totalWords,
      icon: <span className="material-icons-round">translate</span>,
    },
    {
      key: 'sentences',
      title: '句子',
      count: stats.totalSentences,
      icon: <span className="material-icons-round">play_circle</span>,
    },
    {
      key: 'notes',
      title: '笔记',
      count: stats.totalNotes,
      icon: <span className="material-icons-round">edit_note</span>,
    },
  ]), [stats]);

  const favoriteBookPreviews = useMemo(() => {
    if (!favoriteBooks.length) return [];
    const parseTime = (value) => {
      const timestamp = value ? Date.parse(value) : NaN;
      return Number.isFinite(timestamp) ? timestamp : 0;
    };
    return [...favoriteBooks]
      .sort((a, b) => parseTime(b.created_at) - parseTime(a.created_at))
      .slice(0, 3);
  }, [favoriteBooks]);

  useEffect(() => {
    const fetchMenuSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        setBookMetadataError(null);

        const [booksResponse, wordsResponse, sentencesResponse, notesResponse] = await Promise.all([
          api.get('/favorite-books/summary', { params: { limit: 3 } }).catch(() => ({ data: { items: [], total_count: 0 } })),
          api.get('/favorite-words/summary', { params: { limit: 3 } }).catch(() => ({ data: { groups: [], total_count: 0 } })),
          api.get('/favorite-sentences/summary', { params: { limit: 3 } }).catch(() => ({ data: { groups: [], total_count: 0 } })),
          api.get('/user-notes/summary', { params: { limit: 3 } }).catch(() => ({ data: { groups: [], total_count: 0 } })),
        ]);

        const bookItems = Array.isArray(booksResponse.data?.items) ? booksResponse.data.items : [];
        const wordGroups = Array.isArray(wordsResponse.data?.groups) ? wordsResponse.data.groups : [];
        const sentenceGroups = Array.isArray(sentencesResponse.data?.groups) ? sentencesResponse.data.groups : [];
        const noteGroups = Array.isArray(notesResponse.data?.groups) ? notesResponse.data.groups : [];

        setFavoriteBooks(bookItems);
        const menuBookMetadata = {};
        bookItems.forEach((item) => {
          if (!item?.novel_id) return;
          menuBookMetadata[String(item.novel_id)] = {
            title: item.novel_title,
            author: item.novel_author,
            description: item.novel_description,
            cover_image_url: resolveCoverUrl(item.novel_cover_image_url),
          };
        });
        setBookMetadata(menuBookMetadata);

        const normalizeGroup = (group) => ({
          key: String(group.book_id ?? group.bookId ?? 'unknown'),
          bookId: group.book_id ?? group.bookId ?? null,
          bookTitle: group.book_title || group.bookTitle || '未命名书籍',
          itemCount: group.item_count ?? group.itemCount ?? 0,
          items: [],
        });

        setMenuWordGroups(wordGroups.map(normalizeGroup));
        setMenuSentenceGroups(sentenceGroups.map(normalizeGroup));
        setMenuNoteGroups(noteGroups.map(normalizeGroup));

        setStats({
          totalBooks: booksResponse.data?.total_count ?? bookItems.length,
          totalWords: wordsResponse.data?.total_count ?? 0,
          totalSentences: sentencesResponse.data?.total_count ?? 0,
          totalNotes: notesResponse.data?.total_count ?? 0,
        });
      } catch (err) {
        console.error('获取收藏数据失败:', err);
        if (err.response?.status === 401) {
          setError('请先登录查看收藏内容');
        } else {
          setError('获取收藏数据失败，请稍后重试');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMenuSummary();
  }, [refreshToken]);

  const handleDeleteBook = async (favoriteId) => {
    try {
      await api.delete(`/favorite-books/${favoriteId}`);
      setFavoriteBooks((prev) => prev.filter((item) => item.id !== favoriteId));
      setStats((prev) => ({ ...prev, totalBooks: Math.max(0, prev.totalBooks - 1) }));
    } catch (err) {
      console.error('删除收藏书籍失败:', err);
      alert('取消收藏失败，请稍后重试');
    }
  };

  const handleOpenFavoriteBook = (favoriteBook) => {
    if (!favoriteBook?.novel_id) return;
    navigate(`/book?bookid=${favoriteBook.novel_id}`, { state: { from: '/study/books' } });
  };

  if (loading) {
    return (
      <div className={styles.history}>
        <LoadingSpinner fullPage text="加载收藏内容中..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.history}>
        <div className={styles.noHistory}>
          <div className={styles.noHistoryIcon}><AlertIcon size={48} /></div>
          <h3>无法加载收藏内容</h3>
          <p>{error}</p>
          <button
            className={styles.retryBtn}
            onClick={() => setRefreshToken((prev) => prev + 1)}
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.history}>
      <StudyMenu
        cardConfigs={cardConfigs}
        favoriteBookPreviews={favoriteBookPreviews}
        favoriteWordPreviewGroups={menuWordGroups}
        favoriteSentencePreviewGroups={menuSentenceGroups}
        favoriteNotePreviewGroups={menuNoteGroups}
        bookMetadata={bookMetadata}
        completedChapterCounts={{}}
        bookMetadataLoading={bookMetadataLoading}
        bookMetadataError={bookMetadataError}
        onOpenBookDetail={handleOpenFavoriteBook}
        onDeleteBook={handleDeleteBook}
        onOpenBookWords={(group) => {
          if (!group?.bookId) return;
          navigate(`/study/words?bookId=${encodeURIComponent(group.bookId)}`);
        }}
        sentenceExpandedState={{}}
        onToggleSentenceGroup={() => {}}
        onPlayClip={() => {}}
        onStopClip={() => {}}
        playingClip={{}}
        onDeleteSentence={() => {}}
        onStartSentenceStudy={() => {}}
        sentenceLoading={false}
        sentenceError={null}
        noteExpandedState={{}}
        onToggleNoteGroup={() => {}}
        onDeleteNote={() => {}}
        onStartNoteStudy={() => {}}
        noteLoading={false}
        noteError={null}
        onSelectView={(card) => {
          const targetPath = card.key === 'books' ? '/study/books' : `/study/${card.key}`;
          navigate(targetPath);
        }}
      />
    </div>
  );
};

export default StudyMenuPage;


